import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { C3ApiService } from "../../../services/c3-api.service";
import { C3Actions, C3Selectors } from ".";
import { catchError, defaultIfEmpty, exhaustMap, forkJoin, map, mergeMap, Observable, of, switchMap, withLatestFrom } from "rxjs";
import { CoreSelectors } from "../../core";
import { C3ProductBillOfMaterialsLineItem, C3ProductBillOfMaterialsLineItemType, C3ProductType, C3ProductVersion, C3ProductVersionStatus, InventoryTransaction, Resource, SaveStatus } from "@prism/prism-shared";
import { PrismCommonApiService } from "../../../services/prism-common-api.service";

@Injectable({ providedIn: 'root' })
export class C3Effects {
    constructor(
        public store: Store,
        private actions$: Actions,
        private c3ApiService: C3ApiService,
        private commonApiService: PrismCommonApiService,
    ) { }

    private componentGroupContainsFinishedProductRows(
        productVersion: C3ProductVersion,
        persistedLineItems: C3ProductBillOfMaterialsLineItem[],
        editedLineItems: C3ProductBillOfMaterialsLineItem[],
        lineItemIdsToDelete: string[],
        productVersionDict: Record<string, C3ProductVersion>,
    ): boolean {
        if (productVersion.type !== C3ProductType.ComponentGroup) {
            return false;
        }

        return [...persistedLineItems, ...editedLineItems]
            .filter((lineItem) => !lineItemIdsToDelete.includes(lineItem.id))
            .filter((lineItem) => lineItem.type === C3ProductBillOfMaterialsLineItemType.Product)
            .some((lineItem) => (productVersionDict[lineItem.entityId]?.type ?? C3ProductType.Finished) === C3ProductType.Finished);
    }

    SetHardwareItem$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetHardwareItem.initial),
        mergeMap(({ hardwareItem }) => this.c3ApiService.setHardwareItem(hardwareItem).pipe(
            map(() => C3Actions.SetHardwareItem.success({ hardwareItem })),
            catchError((error) => of(C3Actions.SetHardwareItem.failure({ error })))
        ))
    ))

    LoadHardwareItems$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadHardwareItems.initial),
        switchMap(() => this.c3ApiService.watchHardwareItems().pipe(
            map((hardwareItems) => C3Actions.LoadHardwareItems.success({ hardwareItems })),
            catchError((error) => of(C3Actions.LoadHardwareItems.failure({ error })))
        ))
    ))

    LoadHardwareItemById$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadHardwareItemById.initial),
        mergeMap(({ hardwareItemId }) => this.c3ApiService.loadHardwareItemById(hardwareItemId).pipe(
            map((hardwareItem) => C3Actions.LoadHardwareItemById.success({ hardwareItem })),
            catchError((error) => of(C3Actions.LoadHardwareItemById.failure({ error })))
        ))
    ))

    LoadHardwareItemsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadHardwareItemsByIds.initial),
        mergeMap(({ hardwareItemIds }) => this.c3ApiService.loadHardwareItemsByIds(hardwareItemIds).pipe(
            map((hardwareItems) => C3Actions.LoadHardwareItemsByIds.success({ hardwareItems })),
            catchError((error) => of(C3Actions.LoadHardwareItemsByIds.failure({ error })))
        ))
    ))

    LoadRecentHardareInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadRecentHardwareInventoryTransactions.initial),
        mergeMap(() => this.c3ApiService.loadRecentHardwareInventoryTransactions().pipe(
            map((transactions) => C3Actions.LoadRecentHardwareInventoryTransactions.success({ transactions })),
            catchError((error) => of(C3Actions.LoadRecentHardwareInventoryTransactions.failure({ error })))
        ))
    ))

    LoadVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadVendorHardwareData.initial),
        mergeMap(() => this.c3ApiService.loadVendorHardwareData().pipe(
            map((vendorHardwareData) => C3Actions.LoadVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(C3Actions.LoadVendorHardwareData.failure({ error })))
        ))
    ))

    LoadVendorHardwareDataByHardwareItemId$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadVendorHardwareDataByHardwareItemId.initial),
        mergeMap(({ hardwareItemId }) => this.c3ApiService.loadVendorHardwareDataByHardwareItemId(hardwareItemId).pipe(
            map((vendorHardwareData) => C3Actions.LoadVendorHardwareDataByHardwareItemId.success({ vendorHardwareData })),
            catchError((error) => of(C3Actions.LoadVendorHardwareDataByHardwareItemId.failure({ error })))
        ))
    ))

    LoadVendorHardwareDataByHardwareItemIds$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadVendorHardwareDataByHardwareItemIds.initial),
        mergeMap(({ hardwareItemIds }) => this.c3ApiService.loadVendorHardwareDataByHardwareItemIds(hardwareItemIds).pipe(
            map((vendorHardwareData) => C3Actions.LoadVendorHardwareDataByHardwareItemIds.success({ vendorHardwareData })),
            catchError((error) => of(C3Actions.LoadVendorHardwareDataByHardwareItemIds.failure({ error })))
        ))
    ))

    SetHardwareInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C3Actions.SetHardwareInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([{ hardwareId, qtyChange, comment }, currentUser]) => this.c3ApiService.newHardwareInventoryTransaction(
                hardwareId,
                qtyChange,
                comment,
                currentUser,
            ).pipe(
                map((transaction) => C3Actions.SetHardwareInventoryTransaction.success({ transaction })),
                catchError((error) => of(C3Actions.SetHardwareInventoryTransaction.failure({ error })))
            ))
        )
    )

    SetVendorHardwareData$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetVendorHardwareData.initial),
        mergeMap(({ vendorHardwareData }) => this.c3ApiService.setVendorHardwareData(vendorHardwareData).pipe(
            map(() => C3Actions.SetVendorHardwareData.success({ vendorHardwareData })),
            catchError((error) => of(C3Actions.SetVendorHardwareData.failure({ error })))
        ))
    ))

    SetVendorHardwareDataAndHardwareItem$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetVendorHardwareDataAndHardwareItem.initial),
        mergeMap(({ vendorHardwareData, hardwareItem }) => forkJoin([
            this.c3ApiService.setVendorHardwareData(vendorHardwareData),
            this.c3ApiService.setHardwareItem(hardwareItem),
        ]).pipe(
            map(() => C3Actions.SetVendorHardwareDataAndHardwareItem.success({ hardwareItem, vendorHardwareData })),
            catchError((error) => of(C3Actions.SetVendorHardwareDataAndHardwareItem.failure({ error })))
        ))
    ))

    LoadProducts$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadProducts.initial),
        switchMap(() => this.c3ApiService.watchProducts().pipe(
            map((products) => C3Actions.LoadProducts.success({ products })),
            catchError((error) => of(C3Actions.LoadProducts.failure({ error })))
        ))
    ))

    // Both order effects watch rather than fetch: writes go straight through
    // C3ApiService from the components, and the live query is what folds them back
    // into the store.
    LoadOrders$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadOrders.initial),
        switchMap(() => this.c3ApiService.watchOrders().pipe(
            map((orders) => C3Actions.LoadOrders.success({ orders })),
            catchError((error) => of(C3Actions.LoadOrders.failure({ error })))
        ))
    ))

    LoadOrderedProducts$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadOrderedProducts.initial),
        switchMap(() => this.c3ApiService.watchOrderedProducts().pipe(
            map((orderedProducts) => C3Actions.LoadOrderedProducts.success({ orderedProducts })),
            catchError((error) => of(C3Actions.LoadOrderedProducts.failure({ error })))
        ))
    ))

    LoadReleasedAndDraftProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadReleasedAndDraftProductVersions.initial),
        mergeMap(() => this.c3ApiService.loadReleasedAndDraftProductVersions().pipe(
            map((releasedAndDraftProductVersions) => C3Actions.LoadReleasedAndDraftProductVersions.success({ releasedAndDraftProductVersions })),
            catchError((error) => of(C3Actions.LoadReleasedAndDraftProductVersions.failure({ error })))
        ))
    ))

    LoadReleasedProductVersions$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadReleasedProductVersions.initial),
        mergeMap(() => this.c3ApiService.loadReleasedProductVersions().pipe(
            map((productVersions) => C3Actions.LoadReleasedProductVersions.success({ productVersions })),
            catchError((error) => of(C3Actions.LoadReleasedProductVersions.failure({ error })))
        ))
    ))

    SetProduct$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetProduct.initial),
        mergeMap(({ product }) => this.c3ApiService.setProduct(product).pipe(
            map(() => C3Actions.SetProduct.success({ product })),
            catchError((error) => of(C3Actions.SetProduct.failure({ error })))
        ))
    ))

    SetProductVersion$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetProductVersion.initial),
        mergeMap(({ productVersion }) => this.c3ApiService.setProductVersion(productVersion).pipe(
            map(() => C3Actions.SetProductVersion.success({ productVersion })),
            catchError((error) => of(C3Actions.SetProductVersion.failure({ error })))
        ))
    ))

    ViewProductLoadProductData$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.ViewProductLoadProductData.initial),
        mergeMap(({ productId, productVersionId }) => this.c3ApiService.loadProductById(productId).pipe(
            switchMap((selectedProduct) => this.c3ApiService.loadProductVersionsByProductId(productId).pipe(
                switchMap((selectedProductVersions) => {

                    let selectedProductVersion: C3ProductVersion = selectedProductVersions.find((pv) => pv.id === productVersionId) as C3ProductVersion

                    if (!selectedProductVersion) {
                        selectedProductVersion = selectedProductVersions.find((pv) => pv.status === C3ProductVersionStatus.Released) as C3ProductVersion
                    }
                    let drawingResource$: Observable<Resource> = of(null as unknown as Resource)
                    if (!!selectedProductVersion.drawingResourceId) {
                        drawingResource$ = this.commonApiService.loadResourceById(selectedProductVersion.drawingResourceId)
                    }

                    const productVersionsReferencingSelectedProduct$ = this.c3ApiService.loadBillOfMaterialsLineItemsByEntityId(selectedProductVersion.productId).pipe(
                        switchMap((lineItems) => this.c3ApiService.loadProductVersionsByIds(lineItems.map((item) => item.productVersionId)))
                    )

                    const lineItems$ = forkJoin(selectedProductVersions.map((pv) => this.c3ApiService.loadBillOfMaterialsLineItemsByProductVersionId(pv.id))).pipe(
                        defaultIfEmpty([]),
                        map((lineItemGroups) => lineItemGroups.flat())
                    )
                    return forkJoin([
                        drawingResource$,
                        lineItems$
                    ]).pipe(
                        switchMap(([selectedDrawingResource, lineItems]) => {

                            const releasedProductVersions$ = lineItems
                                .filter((li) => li.type === C3ProductBillOfMaterialsLineItemType.Product)
                                .map((li) => this.c3ApiService.loadReleasedProductVersionByProductId(li.entityId))

                            const products$ = lineItems
                                .filter((li) => li.type === C3ProductBillOfMaterialsLineItemType.Product)
                                .map((li) => this.c3ApiService.loadProductById(li.entityId))

                            const hardwareItems$ = lineItems
                                .filter((li) => li.type === C3ProductBillOfMaterialsLineItemType.Hardware)
                                .map((li) => this.c3ApiService.loadHardwareItemById(li.entityId))


                            return forkJoin([
                                forkJoin(releasedProductVersions$).pipe(
                                    defaultIfEmpty([]),
                                    map((releasedProductVersions) => releasedProductVersions.flat())
                                ),
                                forkJoin(products$).pipe(defaultIfEmpty([])),
                                forkJoin(hardwareItems$).pipe(defaultIfEmpty([])),
                                productVersionsReferencingSelectedProduct$,
                            ]).pipe(
                                map(([
                                    releasedProductVersions,
                                    products,
                                    hardwareItems,
                                    productVersionsReferencingSelectedProduct,
                                ]) => C3Actions.ViewProductLoadProductData.success({
                                    selectedProductVersionId: selectedProductVersion.id,
                                    selectedProductVersions,
                                    lineItems,
                                    selectedProduct,
                                    releasedProductVersions,
                                    products,
                                    hardwareItems,
                                    selectedDrawingResource,
                                    productVersionsReferencingSelectedProduct,
                                }))
                            )
                        })
                    )

                }),

                catchError((error) => of(C3Actions.ViewProductLoadProductData.failure({ error })))
            ))
        ))
    ))

    SetBillOfMaterialsLineItems$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.SetBillOfMaterialsLineItems.initial),
        mergeMap(({ lineItems }) => this.c3ApiService.setBillOfMaterialsLineItems(lineItems).pipe(
            map(() => C3Actions.SetBillOfMaterialsLineItems.success({ lineItems })),
            catchError((error) => of(C3Actions.SetBillOfMaterialsLineItems.failure({ error })))
        ))
    ));

    EditProductLoadProductData$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.EditProductLoadProductData.initial),
        mergeMap(({ productVersionId }) => this.c3ApiService.loadProductVersionById(productVersionId).pipe(
            switchMap((productVersion) => this.c3ApiService.loadProductById(productVersion.productId).pipe(
                switchMap((product) => {

                    return forkJoin([
                        this.c3ApiService.loadBillOfMaterialsLineItemsByProductVersionId(productVersionId),
                        this.c3ApiService.loadHardwareItems(),
                        this.c3ApiService.loadProducts(),
                        this.c3ApiService.loadReleasedProductVersions(),
                    ]).pipe(
                        map(([
                            lineItems,
                            hardwareItems,
                            products,
                            releasedProductVersions,

                        ]) => C3Actions.EditProductLoadProductData.success({
                            productVersion,
                            product,
                            lineItems,
                            hardwareItems,
                            products,
                            releasedProductVersions,
                        })),
                        catchError((error) => of(C3Actions.EditProductLoadProductData.failure({ error })))
                    )
                })
            ))
        ))
    ))

    EditProductSaveProduct$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.EditProductSaveProductVersion.initial),
        withLatestFrom(
            this.store.select(C3Selectors.getEditProductProductVersion),
            this.store.select(C3Selectors.getBillOfMaterialsLineItems),
            this.store.select(C3Selectors.getEditProductBillOfMaterialsLineItems),
            this.store.select(C3Selectors.getEditProductBillOfMaterialsLineItemIdsToDelete),
            this.store.select(C3Selectors.getProductIdToProductVersionDict),
            this.store.select(CoreSelectors.getCurrentUser),   
        ),
        mergeMap(([
            _,
            productVersion,
            persistedLineItems,
            lineItems,
            lineItemIdsToDelete,
            productVersionDict,
            currentUser,
        ]) => {
            if (this.componentGroupContainsFinishedProductRows(productVersion, persistedLineItems, lineItems, lineItemIdsToDelete, productVersionDict)) {
                return of(C3Actions.EditProductSaveProductVersion.failure({
                    error: 'component groups cannot contain finished product BOM items'
                }));
            }

            let updatedProductVersion: C3ProductVersion = {
                id: productVersion.id,
                productId: productVersion.productId,
                status: productVersion.status,
                versionNumber: productVersion.versionNumber,
                partNumber: productVersion.partNumber,
                imageUrl: productVersion.imageUrl,
                thumbnailImageUrl: productVersion.thumbnailImageUrl,
                description: productVersion.description,
                customerPartNumber: productVersion.customerPartNumber,
                customerId: productVersion.customerId,
                type: productVersion.type,
                drawingResourceId: productVersion.drawingResourceId,
                createdById: productVersion.createdById,
                createdOn: productVersion.createdOn,
                updatedById: currentUser.id,
                updatedOn: new Date(),
                releasedById: productVersion.releasedById,
                releasedOn: productVersion.releasedOn,
                releaseComment: productVersion.releaseComment,
            }

            return forkJoin([
                this.c3ApiService.setProductVersion(updatedProductVersion),
                this.c3ApiService.setBillOfMaterialsLineItems(lineItems),
                this.c3ApiService.deleteBillOfMaterialsLineItemsByIds(lineItemIdsToDelete)
            ]).pipe(
                map(() => C3Actions.EditProductSaveProductVersion.success({ productVersion })),
                catchError((error) => of(C3Actions.EditProductSaveProductVersion.failure({ error })))
            )
        })
    ))

    EditProductDeleteDraftProductVersion$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.EditProductDeleteDraftProductVersion.initial),
        mergeMap(({ productVersionId }) => this.c3ApiService.deleteProductVersion(productVersionId).pipe(
            map(() => C3Actions.EditProductDeleteDraftProductVersion.success({ productVersionId })),
            catchError((error) => of(C3Actions.EditProductDeleteDraftProductVersion.failure({ error })))
        ))
    ));

    EditProductSetSaveStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                C3Actions.EditProduct.updatepartnumber,
                C3Actions.EditProduct.updatecustomerpartnumber,
                C3Actions.EditProduct.updatecustomerid,
                C3Actions.EditProduct.updatedescription,
                C3Actions.EditProduct.updateproducttype,
                C3Actions.EditProduct.updateimageurl,
                C3Actions.EditProduct.updatethumbnailimageurl,

                C3Actions.EditProduct.addlineitem,
                C3Actions.EditProduct.removelineitem,
                C3Actions.EditProduct.updatelineitem,
                C3Actions.EditProduct.adddrawingresource,
                C3Actions.EditProduct.removedrawingresource,
                C3Actions.EditProduct.addlineitems
            ),
            map(() => C3Actions.SetEditProductSaveStatus({ saveStatus: SaveStatus.Unsaved }))
        )
    )

    EditProductReleaseProductVersion$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.EditProductReleaseProductVersion.initial),
        withLatestFrom(
            this.store.select(CoreSelectors.getCurrentUser),
            this.store.select(C3Selectors.getBillOfMaterialsLineItems),
            this.store.select(C3Selectors.getEditProductBillOfMaterialsLineItems),
            this.store.select(C3Selectors.getEditProductBillOfMaterialsLineItemIdsToDelete),
            this.store.select(C3Selectors.getProductIdToProductVersionDict),
        ),
        exhaustMap(([input, currentUser, persistedLineItems, editedLineItems, lineItemIdsToDelete, productVersionDict]) => {
            if (this.componentGroupContainsFinishedProductRows(input.productVersion, persistedLineItems, editedLineItems, lineItemIdsToDelete, productVersionDict)) {
                return of(C3Actions.EditProductReleaseProductVersion.failure({
                    error: 'component groups cannot contain finished product BOM items'
                }));
            }

            return this.c3ApiService.getReleasedProductVersionCount(input.productVersion.productId).pipe(
                switchMap((releasedProductVersionCount) => {
                    console.log('released version count')
                    return this.c3ApiService.setCurrentReleasedProductVersionToObsolete(input.productVersion.productId).pipe(
                        switchMap(() => {
                            console.log('released -> obsolete')

                            let updatedProductVersion: C3ProductVersion = {
                                id: input.productVersion.id,
                                productId: input.productVersion.productId,
                                partNumber: input.productVersion.partNumber,
                                imageUrl: input.productVersion.imageUrl,
                                thumbnailImageUrl: input.productVersion.thumbnailImageUrl,
                                description: input.productVersion.description,
                                type: input.productVersion.type,
                                customerPartNumber: input.productVersion.customerPartNumber,
                                customerId: input.productVersion.customerId,
                                createdById: input.productVersion.createdById,
                                createdOn: input.productVersion.createdOn,
                                drawingResourceId: input.productVersion.drawingResourceId,
                                status: C3ProductVersionStatus.Released,
                                versionNumber: releasedProductVersionCount + 1,
                                releasedById: currentUser.id,
                                releasedOn: new Date(),
                                updatedById: currentUser.id,
                                updatedOn: new Date(),
                                releaseComment: input.releaseComment,
                            }

                            return this.c3ApiService.setProductVersion(updatedProductVersion).pipe(
                                map(() => C3Actions.EditProductReleaseProductVersion.success({ productVersion: updatedProductVersion })),
                                catchError((error) => of(C3Actions.EditProductReleaseProductVersion.failure({ error })))
                            )
                        })
                    )
                })
            )
        })
    ))

    LoadBillOfMaterialsLineItemsByProductVersionId$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.initial),
        mergeMap(({ productVersionId }) => this.c3ApiService.loadBillOfMaterialsLineItemsByProductVersionId(productVersionId).pipe(
            map((lineItems) => C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.success({ lineItems })),
            catchError((error) => of(C3Actions.LoadBillOfMaterialsLineItemsByProductVersionId.failure({ error })))
        ))
    ))

    ViewProductLoadSelectedProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C3Actions.ViewProductLoadSelectedProduct.initial),
            mergeMap(({ productId }) => this.c3ApiService.loadProductById(productId).pipe(
                map((product) => C3Actions.ViewProductLoadSelectedProduct.success({ product })),
                catchError((error) => of(C3Actions.ViewProductLoadSelectedProduct.failure({ error })))
            ))
        )
    );

    LoadProductsByIds$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadProductsByIds.initial),
        mergeMap(({ productIds }) => this.c3ApiService.loadProductsByIds(productIds).pipe(
            map((products) => C3Actions.LoadProductsByIds.success({ products })),
            catchError((error) => of(C3Actions.LoadProductsByIds.failure({ error })))
        ))
    ))

    SetProductInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C3Actions.SetProductInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([
                {
                    productId,
                    qtyChange,
                    comment,
                    hardwareIdToQuantityChangeDict,
                    productIdToQuantityChangeDict,
                },
                curentUser
            ]) => this.c3ApiService.newProductInventoryTransaction(
                productId,
                qtyChange,
                comment,
                hardwareIdToQuantityChangeDict,
                productIdToQuantityChangeDict,
                curentUser
            ).pipe(
                map((transaction: InventoryTransaction) => C3Actions.SetProductInventoryTransaction.success({
                    transaction,
                    hardwareIdToQuantityChangeDict,
                    productIdToQuantityChangeDict,
                })),
                catchError((error) => of(C3Actions.SetProductInventoryTransaction.failure({ error })))
            ))
        )
    )

    SetComponentGroupBomInventoryTransaction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(C3Actions.SetComponentGroupBomInventoryTransaction.initial),
            withLatestFrom(this.store.select(CoreSelectors.getCurrentUser)),
            mergeMap(([
                {
                    comment,
                    hardwareIdToQuantityChangeDict,
                    productIdToQuantityChangeDict,
                },
                currentUser,
            ]) => this.c3ApiService.newComponentGroupBomInventoryTransaction(
                comment,
                hardwareIdToQuantityChangeDict,
                productIdToQuantityChangeDict,
                currentUser,
            ).pipe(
                map((transactions) => C3Actions.SetComponentGroupBomInventoryTransaction.success({
                    transactions,
                    hardwareIdToQuantityChangeDict,
                    productIdToQuantityChangeDict,
                })),
                catchError((error) => of(C3Actions.SetComponentGroupBomInventoryTransaction.failure({ error }))),
            )),
        )
    )

    LoadRecentProductInventoryTransactions$ = createEffect(() => this.actions$.pipe(
        ofType(C3Actions.LoadRecentProductInventoryTransactions.initial),
        mergeMap(() => this.c3ApiService.loadRecentProductInventoryTransactions().pipe(
            map((transactions) => C3Actions.LoadRecentProductInventoryTransactions.success({ transactions })),
            catchError((error) => of(C3Actions.LoadRecentProductInventoryTransactions.failure({ error })))
        ))
    ));
}
